import { useCallback, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { formatarDataHora } from '../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';

import {
  PERIODO_MAXIMO_EM_DIAS,
  PERIODO_PADRAO_EM_DIAS,
  TAMANHO_DE_PAGINA,
  diasNoPeriodo,
  ehDiaValido,
  ehUuid,
  hojeEmSaoPaulo,
  opcoesDaTrilhaDeAcesso,
  opcoesDosUsuariosDaTrilha,
  somarDias,
  type FiltrosDaTrilha,
} from './api/trilhaDeAcesso';
import {
  ROTULOS_DE_ACAO,
  ROTULOS_DE_PAPEL,
  ROTULOS_DE_RECURSO,
  type AcaoDeAcesso,
  type RecursoAcessado,
  type RegistroDeAcesso,
} from './api/tipos';

type ChaveDeFiltro = 'desde' | 'ate' | 'usuario' | 'acao' | 'recurso' | 'caso' | 'ticket';

const CHAVES_DE_FILTRO_OPCIONAL: readonly ChaveDeFiltro[] = ['usuario', 'acao', 'recurso', 'caso', 'ticket'];

/**
 * Largura de um campo do filtro: parte de `base` px, cresce para dividir a
 * sobra da linha, e nunca fica abaixo de `base` — abaixo disso ele quebra
 * para a linha seguinte. Abaixo de `sm`, ocupa a linha inteira.
 */
function campoDeFiltro(base: number): SxProps<Theme> {
  return { flex: `1 1 ${String(base)}px`, minWidth: { xs: '100%', sm: base } };
}

function ehAcao(valor: string | null): valor is AcaoDeAcesso {
  return valor !== null && Object.hasOwn(ROTULOS_DE_ACAO, valor);
}

function ehRecurso(valor: string | null): valor is RecursoAcessado {
  return valor !== null && Object.hasOwn(ROTULOS_DE_RECURSO, valor);
}

interface EstadoDosFiltros {
  /** O que vai para a API. */
  readonly filtros: FiltrosDaTrilha;
  /** O que a pessoa digitou nos campos de id — pode ainda não ser um UUID. */
  readonly casoDigitado: string;
  readonly ticketDigitado: string;
  /** Mensagem de período inválido; `null` quando dá para consultar. */
  readonly erroDePeriodo: string | null;
}

/** Filtro e página vivem na URL — mesmo motivo de `useFiltrosDaFilaNaUrl` na fila de triagem. */
function useFiltrosDaTrilhaNaUrl(): EstadoDosFiltros & {
  definirPagina: (valor: number) => void;
  definirFiltro: (chave: ChaveDeFiltro, valor: string | null) => void;
  limparFiltros: () => void;
} {
  const [parametros, definirParametros] = useSearchParams();

  const estado = useMemo<EstadoDosFiltros>(() => {
    const hoje = hojeEmSaoPaulo();
    const ateBruto = parametros.get('ate');
    const ate = ehDiaValido(ateBruto) ? ateBruto : hoje;
    const desdeBruto = parametros.get('desde');
    const desde = ehDiaValido(desdeBruto) ? desdeBruto : somarDias(ate, -(PERIODO_PADRAO_EM_DIAS - 1));
    const numero = Number(parametros.get('pagina'));
    const usuario = parametros.get('usuario');
    const acao = parametros.get('acao');
    const recurso = parametros.get('recurso');
    const casoDigitado = parametros.get('caso') ?? '';
    const ticketDigitado = parametros.get('ticket') ?? '';
    const dias = diasNoPeriodo(desde, ate);

    return {
      filtros: {
        pagina: Number.isInteger(numero) && numero >= 1 ? numero : 1,
        desde,
        ate,
        usuarioId: ehUuid(usuario) ? usuario : null,
        acao: ehAcao(acao) ? acao : null,
        recurso: ehRecurso(recurso) ? recurso : null,
        casoId: ehUuid(casoDigitado) ? casoDigitado.trim() : null,
        ticketId: ehUuid(ticketDigitado) ? ticketDigitado.trim() : null,
      },
      casoDigitado,
      ticketDigitado,
      erroDePeriodo:
        dias < 1
          ? 'A data final precisa ser igual ou posterior à inicial.'
          : dias > PERIODO_MAXIMO_EM_DIAS
            ? `O período pode ter no máximo ${String(PERIODO_MAXIMO_EM_DIAS)} dias.`
            : null,
    };
  }, [parametros]);

  const definirPagina = useCallback(
    (valor: number) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (valor <= 1) proximos.delete('pagina');
          else proximos.set('pagina', String(valor));
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  const definirFiltro = useCallback(
    (chave: ChaveDeFiltro, valor: string | null) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (valor) proximos.set(chave, valor);
          else proximos.delete(chave);
          proximos.delete('pagina');
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  // O período fica: "limpar filtros" tira o recorte, não a janela de tempo.
  const limparFiltros = useCallback(() => {
    definirParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        for (const chave of CHAVES_DE_FILTRO_OPCIONAL) proximos.delete(chave);
        proximos.delete('pagina');
        return proximos;
      },
      { replace: true },
    );
  }, [definirParametros]);

  return { ...estado, definirPagina, definirFiltro, limparFiltros };
}

function CelulaDeQuem({ registro }: { registro: RegistroDeAcesso }): React.JSX.Element {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">{registro.login}</Typography>
      <Typography variant="caption" color="text.secondary">
        {registro.tipoDeAtor === 'Integration'
          ? 'Máquina (chave de API)'
          : registro.papel !== null
            ? ROTULOS_DE_PAPEL[registro.papel]
            : 'Sem papel'}
      </Typography>
    </Stack>
  );
}

/**
 * Id de caso ou ticket na linha. Clicar aplica o MESMO filtro que o campo
 * "Id do caso"/"Id do ticket" de cima: a coluna e o filtro são a mesma coisa.
 */
function CelulaDeIdFiltravel({
  valor,
  rotulo,
  aoFiltrar,
}: {
  valor: string | null;
  rotulo: string;
  aoFiltrar: (valor: string) => void;
}): React.JSX.Element {
  if (valor === null) return <Typography variant="body2">—</Typography>;
  return (
    <Button
      size="small"
      variant="text"
      onClick={() => {
        aoFiltrar(valor);
      }}
      aria-label={`Filtrar por este ${rotulo}`}
      title={`Filtrar por este ${rotulo}`}
      sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left' }}
    >
      <Identificador valor={valor} titulo={`Filtrar por este ${rotulo}: ${valor}`} />
    </Button>
  );
}

function CelulaDeStatus({ status }: { status: number }): React.JSX.Element {
  const cor = status >= 500 ? 'error' : status >= 400 ? 'warning' : 'success';
  return <Chip label={String(status)} size="small" color={cor} variant="outlined" />;
}

/**
 * A trilha LGPD de acesso (`NR-114`): quem viu ou alterou dado de paciente,
 * quando e o quê.
 *
 * Aberta a todo usuário logado POR ENQUANTO — o perfilamento vem depois. A
 * tela mostra ids e metadado, nunca o dado do paciente: para saber QUEM é o
 * paciente, abre-se o caso ou o ticket, e essa abertura entra na trilha.
 *
 * Quatro estados, na ordem da fila de triagem: carregando, erro, vazio e
 * dados. Erro SEMPRE antes do vazio.
 */
export function TrilhaDeAcesso(): React.JSX.Element {
  const { filtros, casoDigitado, ticketDigitado, erroDePeriodo, definirPagina, definirFiltro, limparFiltros } =
    useFiltrosDaTrilhaNaUrl();
  const { pagina } = filtros;
  const consulta = useQuery({ ...opcoesDaTrilhaDeAcesso(filtros), enabled: erroDePeriodo === null });
  const usuarios = useQuery(opcoesDosUsuariosDaTrilha());

  const casoInvalido = casoDigitado !== '' && filtros.casoId === null;
  const ticketInvalido = ticketDigitado !== '' && filtros.ticketId === null;
  const algumFiltro =
    filtros.usuarioId !== null ||
    filtros.acao !== null ||
    filtros.recurso !== null ||
    filtros.casoId !== null ||
    filtros.ticketId !== null;

  const trilha = consulta.data;
  const itens = trilha?.itens ?? [];
  const temProximaPagina = trilha !== undefined && pagina * TAMANHO_DE_PAGINA < trilha.total;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Trilha de acesso</Typography>
        <Typography variant="body2" color="text.secondary">
          Quem viu ou alterou dado de paciente, quando e o quê. O registro não pode ser editado nem apagado.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        {/* Em linha até onde couber: cada campo cresce para ocupar a linha e
            quebra quando falta espaço; no celular, um por linha. */}
        <Box
          role="group"
          aria-label="Filtros da trilha"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}
        >
          <TextField
            type="date"
            label="De"
            value={filtros.desde}
            onChange={(evento) => {
              definirFiltro('desde', evento.target.value || null);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={campoDeFiltro(150)}
          />
          <TextField
            type="date"
            label="Até"
            value={filtros.ate}
            onChange={(evento) => {
              definirFiltro('ate', evento.target.value || null);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={campoDeFiltro(150)}
          />
          <TextField
            select
            label="Usuário"
            value={filtros.usuarioId ?? ''}
            onChange={(evento) => {
              definirFiltro('usuario', evento.target.value || null);
            }}
            sx={campoDeFiltro(220)}
          >
            <MenuItem value="">Todos os usuários</MenuItem>
            {(usuarios.data ?? []).map((usuario) => (
              <MenuItem key={usuario.id} value={usuario.id}>
                {usuario.nome} ({usuario.login})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Ação"
            value={filtros.acao ?? ''}
            onChange={(evento) => {
              definirFiltro('acao', evento.target.value || null);
            }}
            sx={campoDeFiltro(150)}
          >
            <MenuItem value="">Todas as ações</MenuItem>
            {(Object.keys(ROTULOS_DE_ACAO) as AcaoDeAcesso[]).map((acao) => (
              <MenuItem key={acao} value={acao}>
                {ROTULOS_DE_ACAO[acao]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Recurso"
            value={filtros.recurso ?? ''}
            onChange={(evento) => {
              definirFiltro('recurso', evento.target.value || null);
            }}
            sx={campoDeFiltro(220)}
          >
            <MenuItem value="">Todos os recursos</MenuItem>
            {(Object.keys(ROTULOS_DE_RECURSO) as RecursoAcessado[]).map((recurso) => (
              <MenuItem key={recurso} value={recurso}>
                {ROTULOS_DE_RECURSO[recurso]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Id do caso"
            value={casoDigitado}
            onChange={(evento) => {
              definirFiltro('caso', evento.target.value || null);
            }}
            error={casoInvalido}
            helperText={casoInvalido ? 'Informe o id completo (UUID) do caso.' : undefined}
            sx={campoDeFiltro(300)}
          />
          <TextField
            label="Id do ticket"
            value={ticketDigitado}
            onChange={(evento) => {
              definirFiltro('ticket', evento.target.value || null);
            }}
            error={ticketInvalido}
            helperText={ticketInvalido ? 'Informe o id completo (UUID) do ticket.' : undefined}
            sx={campoDeFiltro(300)}
          />
        </Box>
      </Paper>

      {erroDePeriodo !== null ? <Alert severity="warning">{erroDePeriodo}</Alert> : null}

      {erroDePeriodo === null ? (
        <Paper variant="outlined">
          {consulta.isPending ? <EstadoDeCarregamento /> : null}

          {consulta.isError ? (
            <EstadoDeErro
              erro={consulta.error}
              contexto="Não foi possível carregar a trilha de acesso"
              aoReprocessar={() => {
                void consulta.refetch();
              }}
            />
          ) : null}

          {consulta.isSuccess && itens.length === 0 ? (
            <EstadoVazio
              titulo={algumFiltro ? 'Nenhum acesso com esses filtros' : 'Nenhum acesso no período'}
              descricao="A API respondeu normalmente; não há registro de acesso que passe no recorte escolhido."
              {...(algumFiltro ? { acao: { rotulo: 'Limpar filtros', aoClicar: limparFiltros } } : {})}
            />
          ) : null}

          {consulta.isSuccess && itens.length > 0 ? (
            <TableContainer>
              <Table aria-label="Trilha de acesso">
                <TableHead>
                  <TableRow>
                    <TableCell>Quando</TableCell>
                    <TableCell>Quem</TableCell>
                    <TableCell>Ação</TableCell>
                    <TableCell>Recurso</TableCell>
                    <TableCell>Caso</TableCell>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        title="Código que liga esta ação ao registro do sistema. Copie e envie ao suporte para investigar."
                      >
                        Código de rastreio
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((registro) => (
                    <TableRow key={registro.id} hover>
                      <TableCell>{formatarDataHora(registro.ocorridoEm)}</TableCell>
                      <TableCell>
                        <CelulaDeQuem registro={registro} />
                      </TableCell>
                      <TableCell>{ROTULOS_DE_ACAO[registro.acao]}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{ROTULOS_DE_RECURSO[registro.recurso]}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            <code>
                              {registro.metodo} {registro.rota}
                            </code>
                          </Typography>
                          {/* Anexo, e-mail ou campo: o item exato dentro do caso/ticket. */}
                          {registro.recursoId !== null ? (
                            <Typography variant="caption" component="div">
                              Id: <Identificador valor={registro.recursoId} />
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <CelulaDeIdFiltravel
                          valor={registro.casoId}
                          rotulo="caso"
                          aoFiltrar={(valor) => {
                            definirFiltro('caso', valor);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CelulaDeIdFiltravel
                          valor={registro.ticketId}
                          rotulo="ticket"
                          aoFiltrar={(valor) => {
                            definirFiltro('ticket', valor);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CelulaDeStatus status={registro.status} />
                      </TableCell>
                      <TableCell>
                        <Identificador
                          valor={registro.correlationId}
                          titulo="Código de rastreio: clique para selecionar e copiar"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Paper>
      ) : null}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          disabled={pagina <= 1}
          onClick={() => {
            definirPagina(pagina - 1);
          }}
        >
          Página anterior
        </Button>
        <Typography variant="body2" color="text.secondary">
          Página {pagina}
          {trilha !== undefined
            ? ` de ${String(Math.max(1, Math.ceil(trilha.total / TAMANHO_DE_PAGINA)))} — ${String(trilha.total)} no total`
            : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={!temProximaPagina}
          onClick={() => {
            definirPagina(pagina + 1);
          }}
        >
          Próxima página
        </Button>
      </Stack>
    </Stack>
  );
}
